import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { mergeMap, map, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { SchemaService } from './schema.service';
import { SchemaProject } from 'src/app/admin/models/schema-project.model';
import { Schema } from 'src/app/admin/models/schema.model';
import { Router } from '@angular/router';
import {
  GoToCurrentSchemaProject,
  LoadActiveSchema,
  CreateSchema,
  SetSchemaError,
  LoadSchemaProjects,
  SetSchemaProjects,
  LoadCurrentSchemaProject,
  SetCurrentSchemaProject,
  CreateSchemaProject,
  DeleteSchemaProject,
  LoadCurrentSchemas,
  SetCurrentSchemas,
  LoadCurrentSchema,
  SetCurrentSchema,
  LoadSchemaDefinitions,
  SetSchemaDefinitions,
  LoadSchemaDefinitionVersions,
  SetSchemaDefinitionVersions,
  LoadSchemaDefinition,
  SetSchemaDefinition,
  CreateSchemaDefinition,
  GoToSchemaDefinition,
  DeleteSchemaDefinition,
  SetActiveSchema,
} from './schema.actions';
import { SchemaDefinition } from 'src/app/admin/models/schema-definition.model';

@Injectable()
export class SchemaEffects {
  loadSchemaDefinitions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadSchemaDefinitions),
      mergeMap(({ projectId }) =>
        this.schemaService.loadSchemaDefinitions(projectId).pipe(
          map((definitions: SchemaDefinition[]) => SetSchemaDefinitions({ definitions })),
          catchError((error) => of(SetSchemaError({ error }))),
        ),
      ),
    ),
  );

  loadSchemaDefinitionVersions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadSchemaDefinitionVersions),
      mergeMap(({ projectId, name }) =>
        this.schemaService.loadSchemaDefinitionVersions(projectId, name).pipe(
          map((definitions: SchemaDefinition[]) =>
            SetSchemaDefinitionVersions({ definitionVersions: definitions }),
          ),
          catchError((error) => of(SetSchemaError({ error }))),
        ),
      ),
    ),
  );

  loadSchemaDefinition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadSchemaDefinition),
      mergeMap(({ id }) =>
        this.schemaService.loadSchemaDefinition(id).pipe(
          map((definition: SchemaDefinition) => SetSchemaDefinition({ definition })),
          catchError((error) => of(SetSchemaError({ error }))),
        ),
      ),
    ),
  );

  createSchemaDefinition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateSchemaDefinition),
      mergeMap(({ projectId, name, title, description, values }) =>
        this.schemaService.createSchemaDefinition(projectId, name, title, description, values).pipe(
          map((definition: SchemaDefinition) => GoToSchemaDefinition({ definition })),
          catchError((error) => of(SetSchemaError({ error }))),
        ),
      ),
    ),
  );

  goToSchemaDefinition$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GoToSchemaDefinition),
        tap((action) => {
          const { schemaProjectId, name, id } = action.definition;
          this.router.navigate([
            '/admin',
            'schema-projects',
            schemaProjectId,
            'definitions',
            name,
            id,
          ]);
        }),
      ),
    { dispatch: false },
  );

  deleteSchemaDefinition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteSchemaDefinition),
      mergeMap(({ definitionId, projectId }) =>
        this.schemaService.deleteSchemaDefinition(definitionId).pipe(
          map(() => LoadSchemaDefinitions({ projectId })),
          catchError((error) => of(SetSchemaError({ error }))),
        ),
      ),
    ),
  );

  loadSchemaProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadSchemaProjects),
      mergeMap(() =>
        this.schemaService.loadProjects().pipe(
          map((projects: SchemaProject[]) => SetSchemaProjects({ projects })),
          catchError((error) => of(SetSchemaError({ error }))),
        ),
      ),
    ),
  );

  getSchemaProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadCurrentSchemaProject),
      mergeMap(({ id }) =>
        this.schemaService.getSchemaProject(id).pipe(
          map((project: SchemaProject) => SetCurrentSchemaProject({ project })),
          catchError((error) => of(SetSchemaError({ error }))),
        ),
      ),
    ),
  );

  createSchemaProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateSchemaProject),
      mergeMap(({ name }) =>
        this.schemaService.createProject(name).pipe(
          map(() => LoadSchemaProjects()),
          catchError((error) => of(SetSchemaError({ error }))),
        ),
      ),
    ),
  );

  deleteSchemaProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteSchemaProject),
      mergeMap(({ id }) =>
        this.schemaService.deleteProject(id).pipe(
          map(() => LoadSchemaProjects()),
          catchError((error) => of(SetSchemaError({ error }))),
        ),
      ),
    ),
  );

  loadSchemas$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadCurrentSchemas),
      mergeMap(({ projectId }) =>
        this.schemaService.loadSchemas(projectId).pipe(
          map((schemas: Schema[]) => SetCurrentSchemas({ schemas })),
          catchError((error) => of(SetSchemaError({ error }))),
        ),
      ),
    ),
  );

  loadSchema$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadCurrentSchema),
      mergeMap(({ schemaId }) =>
        this.schemaService.loadSchema(schemaId).pipe(
          map((schema: Schema) => SetCurrentSchema({ schema })),
          catchError((error) => of(SetSchemaError({ error }))),
        ),
      ),
    ),
  );

  createSchema$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateSchema),
      mergeMap(({ projectId, description, values }) =>
        this.schemaService.createSchema(projectId, description, values).pipe(
          map(() => GoToCurrentSchemaProject({ projectId })),
          catchError((error) => of(SetSchemaError({ error }))),
        ),
      ),
    ),
  );

  goToCurrentSchemaProject$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GoToCurrentSchemaProject),
        tap(({ projectId }) =>
          this.router.navigate(['/admin', 'schema-projects', projectId, 'schemas']),
        ),
      ),
    { dispatch: false },
  );

  loadActiveSchema$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadActiveSchema),
      mergeMap(() =>
        this.schemaService.loadActiveSchema().pipe(
          map((schema: Schema) => SetActiveSchema({ schema })),
          catchError((error) => of(SetSchemaError({ error }))),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private schemaService: SchemaService,
    private router: Router,
  ) {}
}
